import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Divider, IconButton, MenuItem, Select, Stack, Typography,
} from '@mui/material'
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import useWallets from '@hooks/useWallets'
import {
  Link, LinkLabel, RunLabel, canRequestHaltClear, marketOf, runStateLabel, settlementOf,
  statusChangeIntent, unrealizedOf,
} from '@util/wallet'

/**
 * 지갑 화면.
 *
 * 사람이 여기서 하는 일은 둘이다 -- **정지된 지갑에 "다 정리했다"고 신고하는 것**과
 * **가동 상태를 바꾸는 것**. 나머지는 전부 읽기다. 그래서 이 화면의 어려움은 버튼이 아니라
 * "지금 무슨 상태인지 헷갈리지 않게 보여주는 것"에 있다 -- 아래 셋은 서로 다른 사건인데
 * 겉보기가 비슷하다.
 *
 *   트레이더가 안 뜸 / 브로커 연결이 끊김 / 정지됨
 *
 * 한 칸에 접으면 사람이 엉뚱한 조치를 한다. 그래서 뱃지가 세 개다.
 *
 * 판정은 전부 `@util/wallet`에 있고 데이터는 전부 `@hooks/useWallets`에 있다. 이 파일은
 * 조립과 문구만 맡는다 -- 여기서 조건을 다시 계산하기 시작하면 그 규칙이 곧 복사된다.
 */

const num = (v, digits = 4) =>
  (v === null || v === undefined) ? '—' : Number(v).toFixed(digits)

/** 돈/가격은 자릿수를 끊어 준다 -- 62150.3과 6215.03을 한눈에 가르는 건 쉼표뿐이다. */
const money = (v, digits = 2) =>
  (v === null || v === undefined)
    ? '—'
    : Number(v).toLocaleString('ko-KR', { minimumFractionDigits: digits, maximumFractionDigits: digits })

/** 손익·등락은 **부호를 항상 붙인다.** 양수에 +가 없으면 "0에서 얼마"인지가 안 읽힌다. */
const signed = (v, digits = 2) =>
  (v === null || v === undefined) ? '—' : `${v >= 0 ? '+' : ''}${money(v, digits)}`

/** 색은 부호로만 정한다 -- 0은 중립이다(이익도 손실도 아니다). */
const pnlColor = (v) => {
  if (v === null || v === undefined || v === 0) {
    return 'text.primary'
  }
  return v > 0 ? 'success.main' : 'error.main'
}

const ago = (ms) => {
  if (!ms) {
    return ''
  }
  const sec = Math.floor((Date.now() - ms) / 1000)
  if (sec < 60) {
    return `${sec}초 전`
  }
  if (sec < 3600) {
    return `${Math.floor(sec / 60)}분 전`
  }
  return `${Math.floor(sec / 3600)}시간 전`
}

const linkColor = (link) => {
  if (link === Link.OK) {
    return 'success'
  }
  if (link === Link.DOWN) {
    return 'error'
  }
  return 'warning'
}

const WalletPage = () => {
  const navigate = useNavigate()
  const { userId } = useParams()
  const {
    wallets, orphans, master, totals, connected, serviceError,
    loading, error, busyId, statuses, requestHaltClear, updateStatus,
  } = useWallets(userId)

  /**
   * 확인 대기 중인 status 변경. `null`이면 창이 닫힌 것이다.
   *
   * **선택과 반영을 갈라놓는 장치다.** 드롭다운의 `value`는 끝까지 서버 값(`run`)에 묶여
   * 있고, 고르는 행위는 여기에만 담긴다. 그래서 확인을 취소하거나 요청이 실패해도
   * 되돌릴 것이 없다 -- 화면은 애초에 안 바뀐 상태다.
   *
   * `{ walletId, next, intent }` 꼴. `intent`를 열 때 한 번 계산해 담아두는 이유는, 창이
   * 떠 있는 동안 스냅샷이 갱신되면(5초) 문구가 눈앞에서 바뀌기 때문이다 -- 사람이 읽던
   * 문장이 바뀌는 건 확인 창에서 제일 나쁜 일이다.
   */
  const [pending, set_pending] = useState(null)

  const closePending = () => set_pending(null)

  const confirmPending = async () => {
    if (!pending) {
      return
    }
    const { walletId, next } = pending
    // 먼저 닫는다 -- 요청이 도는 동안 창을 열어두면 확인 버튼을 두 번 누를 수 있다.
    // 진행 표시는 아래 드롭다운이 busyId로 잠기는 것으로 대신한다.
    closePending()
    await updateStatus(walletId, next)
  }

  /** 정지 안내 -- 정지된 지갑에만 그린다.
   *
   * 거절 사유를 **원문 그대로** 보여준다. 트레이더가 쓰는 문장에 "1분 안에 자동 정리되니
   * 그때 다시 요청하라" 같은 다음 행동이 이미 들어 있어서, 요약하면 그게 날아간다. */
  const renderHalt = (w) => {
    const { halt, link } = w
    const gate = canRequestHaltClear(link, halt)

    return (
      <Alert severity='error' sx={{ mt: 1 }}>
        <Typography variant='body2'>
          <b>거래 정지 -- {halt.code}</b> {halt.at ? `(${ago(halt.at)})` : ''}
        </Typography>

        {/* 정지 사유 원문. 거절된 뒤에는 이 칸이 거절 사유로 덮여 있다(같은 컬럼을 쓴다). */}
        {halt.detail && (
          <Typography variant='caption' component='div' sx={{ mt: 0.5 }}>
            {halt.detail}
          </Typography>
        )}

        {halt.pending && (
          <Typography variant='caption' component='div' sx={{ mt: 0.5 }}>
            해제 요청 접수됨 ({ago(halt.reqAt)}) -- 판정 대기 중
          </Typography>
        )}

        {halt.rejectedReason && !halt.pending && (
          <Typography variant='caption' component='div' sx={{ mt: 0.5 }}>
            직전 해제 요청이 거절됐습니다.
            {halt.restartRequired
              ? ' 이 사유는 재기동으로만 풀립니다.'
              : ' 사유를 해소한 뒤 다시 요청하세요.'}
          </Typography>
        )}

        <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 1 }}>
          <Button
            color='error'
            disabled={!gate.ok || busyId === w.walletId}
            onClick={() => requestHaltClear(w.walletId)}
          >
            {busyId === w.walletId ? '요청 중...' : '해제 요청'}
          </Button>
          {/* 비활성 버튼만 있고 왜 그런지가 없으면 사람은 화면이 고장 난 줄 안다. */}
          {!gate.ok && gate.why && (
            <Typography variant='caption'>{gate.why}</Typography>
          )}
        </Stack>
      </Alert>
    )
  }

  /**
   * 가동 상태 -- 읽기와 쓰기를 **같은 자리**에 둔다.
   *
   * **텍스트 입력을 두지 않는다.** 값이 경로에 실려 서버로 가고, 오타가 통과하면
   * `rejectedByStatus`가 조용히 진입만 막는다(서버도 거르지만 화면이 오타를 낼 이유가 없다).
   * 고를 수 있는 값은 서버가 준 목록뿐이다.
   *
   * 목록을 못 받았으면 **읽기 전용 칩으로 되돌아간다** -- 빈 드롭다운을 내밀면 사람은
   * 화면이 고장 난 줄 안다. 값 자체는 DB에서 왔으므로 보여주는 데는 문제가 없다.
   *
   * 고르는 즉시 쏘지 않는다. `onChange`는 확인 창을 열 뿐이고, 실제 요청은 거기서 나간다.
   */
  const renderRun = (w, settle) => {
    const { run, link, halt } = w
    // **고를 이름과 보여줄 이름이 다르다**(`RunLabel` vs `runStateLabel`). FIN은 정리 중과
    // 종료됨이 갈리는데, 드롭다운 항목은 도달할 상태 하나여야 한다 -- 근거는 util 쪽 주석.
    const label = runStateLabel(run, settle)

    if (statuses.length === 0) {
      return (
        <Chip
          size='small'
          label={label}
          color={run === 'ACTIVE' ? 'default' : 'warning'}
          variant='outlined'
        />
      )
    }

    return (
      <Select
        value={run}
        disabled={busyId === w.walletId}
        onChange={(e) => {
          const next = e.target.value
          if (next === run) {
            return
          }
          set_pending({ walletId: w.walletId, next, intent: statusChangeIntent(next, link, halt, run) })
        }}
        sx={{ minWidth: 148 }}
        // 서버가 허용값을 늘렸는데 화면에 RunLabel이 없으면 원문 코드를 그대로 보여준다.
        // 빈 칸으로 두면 "값이 없다"로 읽힌다.
        renderValue={(v) => (v === run ? label : (RunLabel[v] || v))}
      >
        {statuses.map((s) => (
          <MenuItem key={s} value={s}>{RunLabel[s] || s}</MenuItem>
        ))}
      </Select>
    )
  }

  /**
   * 종료된 지갑 안내 + 거래소 실물.
   *
   * **"포지션 없음"과 나란히 두면 안 되는 정보다.** 종료된 지갑의 `position`은 항상 비어
   * 있는데(엔진 것이 없다는 뜻), 거래소엔 사람이 연 게 있을 수 있다. 그 둘을 같은 자리에
   * 그리면 "이 지갑이 포지션을 들고 있다"로 읽힌다 -- FIN의 정의를 화면이 뒤집는 것이다.
   *
   * 그래서 별도 박스에, **"엔진 소유가 아니다"를 문장으로** 적는다. 색도 info다 -- 사고가
   * 아니라 사실이라 error/warning을 쓰면 사람이 조치를 찾는다.
   */
  const renderSettled = (settle) => {
    if (!settle.settled) {
      return null
    }
    return (
      <Alert severity='info' sx={{ mt: 1 }}>
        <Typography variant='body2'>
          <b>종료됨</b> -- 이 지갑의 거래는 더 이상 하지 않습니다(관측만 합니다)
        </Typography>

        {settle.has ? (
          <>
            <Typography variant='caption' component='div' sx={{ mt: 0.5 }}>
              거래소에 포지션이 있습니다 -- <b>엔진이 연 것이 아닙니다.</b> 트레이더는 관여하지 않습니다.
            </Typography>
            <Typography
              variant='body2'
              sx={{ mt: 0.5, opacity: settle.stale ? 0.45 : 1 }}
            >
              {settle.side} {num(settle.qty, 8)} @ {money(settle.entryPrice)}
              {settle.markPrice != null && ` · mark ${money(settle.markPrice)}`}
              {settle.pnl != null && (
                <Typography component='span' variant='body2' color={pnlColor(settle.pnl)}>
                  {` · ${signed(settle.pnl)}`}
                </Typography>
              )}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {ago(settle.at)} 관측{settle.stale ? ' · 조회 지연' : ''}
            </Typography>
          </>
        ) : (
          <Typography variant='caption' component='div' sx={{ mt: 0.5 }}>
            거래소에도 포지션이 없습니다 ({settle.at ? `${ago(settle.at)} 관측` : '관측 대기'})
          </Typography>
        )}
      </Alert>
    )
  }

  const renderPosition = (position) => {
    if (!position) {
      return <Typography variant='body2'>포지션 없음</Typography>
    }
    return (
      <Typography variant='body2'>
        {position.side} {num(position.qty, 8)} @ {money(position.entryPrice)}
        {' '}stop={money(position.stopPrice)}
        {position.trailPrice ? ` trail=${money(position.trailPrice)}` : ''}
        {' '}({position.strategyName}/{position.signalTf})
      </Typography>
    )
  }

  /**
   * 미실현 손익 줄.
   *
   * **두 값의 출처와 기준 시각이 다르다.** 손익·비율은 거래소가 mark price로 계산한 값이고
   * (`unrealizedAt`), 등락률은 트레이더가 1분봉 종가로 계산한 값이다(`priceAt`). 한 줄에
   * 나란히 두되 **기준 시각을 각각** 보여줘야 한다 -- 하나로 합치면 둘 중 하나는 거짓말이다.
   *
   * 낡았다고 값을 지우지 않는다. 지우면 사람이 "손익이 0이 됐다"로 읽는다 -- 흐리게 하고
   * 나이를 붙이는 게 맞다(낡은 스냅샷을 서버가 안 거르는 것과 같은 방침).
   */
  const renderPnl = (market, unreal) => {
    if (!unreal.has && market.changePct == null) {
      // 포지션은 있는데 손익이 아직 없다 -- 진입 직후 최대 1분, 또는 미결이 걸려 갱신이 멈춘 구간.
      return (
        <Typography variant='body2' color='text.secondary'>
          미실현 확인 중...
        </Typography>
      )
    }
    return (
      <Stack direction='row' spacing={1.5} alignItems='baseline' flexWrap='wrap'>
        {unreal.has && (
          <Typography
            variant='body2'
            color={pnlColor(unreal.pnl)}
            sx={{ opacity: unreal.stale ? 0.45 : 1 }}
          >
            <b>미실현 {signed(unreal.pnl)}</b>
            {unreal.ratio != null && ` (${signed(unreal.ratio * 100)}%)`}
          </Typography>
        )}
        {market.changePct != null && (
          <Typography
            variant='body2'
            color={pnlColor(market.changePct)}
            sx={{ opacity: market.stale ? 0.45 : 1 }}
          >
            등락 {signed(market.changePct)}%
          </Typography>
        )}
        {/* 기준가가 둘이라 헷갈릴 자리다 -- 손익 쪽 기준가를 작게 붙여 둔다. */}
        {unreal.markPrice != null && (
          <Typography variant='caption' color='text.secondary'>
            mark {money(unreal.markPrice)}
            {unreal.at ? ` · ${ago(unreal.at)}` : ''}
          </Typography>
        )}
      </Stack>
    )
  }

  /**
   * 지갑 하나.
   *
   * **배선이 정상이 아니면 숫자를 흐리게 한다.** 서버가 낡은 스냅샷도 거르지 않고 보내므로
   * (그래야 웹이 "대상 없음"과 "갱신 멈춤"을 구분할 수 있다) 낡은 값이 실시간처럼 보이지
   * 않게 하는 건 화면 몫이다. 지우지는 않는다 -- 마지막으로 알던 값이 사고 조사에 필요하다.
   */
  const renderWallet = (w) => {
    // run은 renderRun이 w에서 직접 꺼낸다 -- 여기서 또 꺼내면 안 쓰는 이름이 남는다.
    const { row, snap, link, halt } = w
    const dim = link !== Link.OK

    // 판정은 util이 한다 -- 여기서 다시 계산하면 user.page 목록에 같은 표시를 붙일 때 복사된다.
    const nowMs = Date.now()
    const market = marketOf(snap, nowMs)
    const unreal = unrealizedOf(snap, nowMs)
    const settle = settlementOf(snap, nowMs)

    return (
      <div key={w.walletId} style={{ marginBottom: 16 }}>
        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
          <Typography variant='subtitle1'><b>{row.name}</b></Typography>
          <Typography variant='caption'>#{row.symbolId} / {row.tf} / {row.runMode}</Typography>
          <Chip size='small' label={LinkLabel[link]} color={linkColor(link)} variant='outlined' />
          {renderRun(w, settle)}
          {halt.halted && <Chip size='small' label='정지' color='error' />}
        </Stack>

        {/* 언제 받은 값인지 -- 흐린 숫자를 보고 "언제 것이냐"를 바로 묻게 된다. */}
        {snap && dim && (
          <Typography variant='caption' color='text.secondary'>
            마지막 수신 {ago(snap.uptAt)}
          </Typography>
        )}

        <div style={{ opacity: dim ? 0.45 : 1 }}>
          {/* 시세는 **포지션과 무관하게** 뜬다 -- 심볼의 사실이라 지갑 레벨에 실려 온다.
              그래서 플랫인 지갑에서도 "지금 얼마인가"를 볼 수 있다. */}
          <Stack direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
            <Typography variant='body2' sx={{ opacity: market.stale ? 0.45 : 1 }}>
              시세 {money(market.price)}
            </Typography>
            {market.at && (
              <Typography variant='caption' color='text.secondary'>
                {ago(market.at)} 봉{market.stale ? ' · 수집 지연' : ''}
              </Typography>
            )}
          </Stack>

          {/* [2026-08] 잔고는 **`wallets.balance`(잔고 미러)**다 -- 트레이더가 거래소를 부른
              자리마다(기동·진입·청산·60분 스냅샷) 적어두는 실잔고다.

              옛 화면은 여기에 **dd 기준잔고**를 그렸다. 그건 잔고가 아니라 측정 앵커라서
              첫 진입 전에는 값 자체가 없었고("거래 전이라 기준잔고가 아직 없습니다"),
              입출금은 다음 진입까지 안 보였다. 그래서 축을 갈랐다.

              **기준 시각을 안 붙인다.** 잔고는 체결이 있을 때만 변하고 위 네 자리가 그
              사건을 전부 덮으므로, 낡는 게 아니라 변할 일이 없어서 그대로인 값이다.
              시세·미실현이 시각을 다는 건 성격이 다르다 -- 그 둘은 한 스냅샷 안에서 출처가
              갈려(거래소 mark price vs 1분봉 종가) 서로 기준 시각이 어긋난다.

              트레이더가 멈춰 갱신이 끊긴 경우는 이 줄이 아니라 **카드 단위**로 말한다
              (`linkOf`의 미기동/신선도) -- 그때는 잔고만이 아니라 카드 전체가 낡은 것이다.

              ⚠ **`0`은 "계좌가 비었다"는 사실이다.** 미기동 지갑은 스냅샷 자체가 없어
              (`mergeWallets`의 `snap = null`) 이 갈래에 안 들어온다. 즉 여기서 보는 0은
              트레이더가 거래소에 물어서 받아온 0이다. */}
          <Stack direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
            <Typography variant='body2'>
              잔고 {money(snap?.balance)} / dd {num(snap?.ddRatio)}
              {snap?.signalTf ? ` / 신호 ${snap.signalTf}` : ''}
            </Typography>
            {snap && snap.balance === 0 && (
              <Typography variant='caption' color='error.main'>
                계좌가 비었습니다
              </Typography>
            )}
          </Stack>

          {renderPosition(snap?.position)}
          {snap?.position && renderPnl(market, unreal)}

          <Typography variant='body2'>
            미결 주문 {snap?.orders?.length ?? 0}건
          </Typography>
        </div>

        {renderSettled(settle)}
        {halt.halted && renderHalt(w)}
        <Divider sx={{ mt: 1 }} />
      </div>
    )
  }

  return (
    <>
      {userId && (
        <Stack direction='row' alignItems='center' spacing={1}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackOutlinedIcon />
          </IconButton>
          <Typography variant='body2'>userId {userId} 의 지갑</Typography>
        </Stack>
      )}

      {/* 층이 다른 세 가지 실패를 각각 말한다. 하나로 합치면 어디를 고쳐야 할지가 사라진다. */}
      {!connected && (
        <Alert severity='warning' sx={{ mb: 1 }}>
          브로커 연결 끊김 -- 실시간 값이 갱신되지 않습니다(트레이더는 정상일 수 있습니다)
        </Alert>
      )}
      {serviceError && (
        <Alert severity='error' sx={{ mb: 1 }}>트레이더 오류: {serviceError}</Alert>
      )}
      {error && (
        <Alert severity='error' sx={{ mb: 1 }}>{error}</Alert>
      )}

      {/* DB에 없는데 스냅샷이 오는 지갑. 지운 지갑을 트레이더가 아직 돌리고 있다는 뜻이라
          목록에서 조용히 빼면 안 된다 -- 실거래가 도는데 아무 화면에도 안 나온다. */}
      {orphans.length > 0 && (
        <Alert severity='error' sx={{ mb: 1 }}>
          목록에 없는 지갑이 가동 중입니다 (walletId {orphans.map((o) => o.walletId).join(', ')})
        </Alert>
      )}

      {loading && <Typography variant='body2'>불러오는 중...</Typography>}
      {!loading && wallets.length === 0 && (
        <Typography variant='body2'>등록된 지갑이 없습니다</Typography>
      )}

      {/* 확정 잔고 합.
          **"총자산"이라 부르지 않는다.** 포지션 보유 중의 평가손익이 안 들어가 있어서다
          (`totalsOf` 주석). 이름이 값보다 앞서 나가면 사람이 이 숫자로 손익을 읽는다.

          선물과 현물을 갈라 적는 이유도 같다 -- 합쳐서 하나만 내면 `savingRatio`로 돈이
          마스터에 쌓이는 게 안 보이고, 서브 합이 줄어든 것이 손실인지 이체인지 구별되지
          않는다. 그 구별이 이 화면을 만든 이유다. */}
      {!loading && wallets.length > 0 && (
        <div>
          <Stack direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
            <Typography variant='body2'>
              <b>확정 잔고 합 {money(totals.total)}</b>
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              선물 {money(totals.futures)} (지갑 {totals.walletCount}개)
              {' · '}
              현물 {totals.spotMissing ? '—' : money(totals.spot)}
            </Typography>
          </Stack>

          {/* 마스터 줄은 **세 상태를 갈라 말한다** -- 계좌 없음 / 관측 대기 / 정상.
              하나로 접으면 "마스터를 안 만들었다"와 "관측이 안 돌고 있다"가 같은 빈칸이
              되는데, 뒤쪽은 고쳐야 하는 사건이다(MasterBalanceFetcher가 멎었다는 뜻). */}
          {master?.account && (
            <Stack direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
              <Typography variant='caption' color='text.secondary'>
                마스터 {master.account.name}
              </Typography>
              {master.balance ? (
                <>
                  {/* "관측"이 아니라 **최종**이다. 마스터는 값이 안 바뀌면 행을 안 만들어서
                      (`MasterBalanceService.addBalance`) 이 시각은 마지막으로 **값이 바뀐**
                      때다. 관측 자체는 5분마다 계속 돈다 -- "관측"이라 쓰면 그동안 안 본 것처럼
                      읽힌다. */}
                  <Typography variant='caption' color='text.secondary'>
                    {money(master.balance.balance)} {master.balance.asset}
                    {' · '}
                    {ago(master.balance.regAt)} 최종
                  </Typography>
                  {/* balance와 free가 벌어져 있으면 주문에 잠겼거나 Earn에 들어간 돈이다.
                      정상이면 둘이 같아서 이 칩은 안 뜬다 -- 뜨는 것 자체가 신호다. */}
                  {master.balance.balance !== master.balance.free && (
                    <Typography variant='caption' color='warning.main'>
                      가용 {money(master.balance.free)} -- 잠김/Earn {money(master.balance.balance - master.balance.free)}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant='caption' color='warning.main'>
                  관측 대기 -- 마스터 잔고 기록이 아직 없습니다
                </Typography>
              )}
            </Stack>
          )}

          {/* 총액 **밖**의 몫 -- 지금은 테스트넷.
              버리지 않고 따로 내는 이유: 청산·이체 시험 결과를 보려면 그 잔고도 봐야 한다.
              **"합계에 안 들어감"을 글자로 적는 게 짝이다** -- 숫자만 나란히 두면 사람이
              눈으로 더한다. 실제로 이 줄이 없던 동안 테스트넷 잔고가 합계에 섞여 실계좌가
              비었다는 사실을 가리고 있었다.
              해당 지갑이 하나도 없으면 줄 자체가 안 뜬다(판정은 `totalsOf`가 한다). */}
          {totals.excluded.count > 0 && (
            <Stack direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
              <Typography variant='caption' color='text.secondary'>
                {totals.excluded.modes.join('/')} {money(totals.excluded.futures)}
                {' '}(지갑 {totals.excluded.count}개)
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                -- 합계에 안 들어감
              </Typography>
            </Stack>
          )}
          <Divider sx={{ mt: 1, mb: 1 }} />
        </div>
      )}

      {wallets.map(renderWallet)}

      {/* 확인 창.
          **셋 다 확인을 받되 FIN만 빨갛다.** 문구까지 같게 두면 확인이 요식이 되어 정작
          청산에서 안 멈춘다. 문구와 위험 판정은 statusChangeIntent가 만든다 -- 여기서
          조건을 다시 보기 시작하면 그 규칙이 둘로 갈린다. */}
      <Dialog open={Boolean(pending)} onClose={closePending}>
        <DialogTitle>{pending?.intent.title}</DialogTitle>
        <DialogContent>
          {pending?.intent.lines.map((line, i) => (
            <DialogContentText
              key={i}
              // 첫 줄이 "무슨 일이 벌어지는가"고 나머지는 단서다. 파괴적 변경은 그 첫 줄을
              // 붉게 세운다 -- 사람이 제목만 보고 누르는 걸 한 번 더 붙잡는다.
              color={i === 0 && pending?.intent.destructive ? 'error' : 'text.primary'}
              sx={{ mt: i === 0 ? 0 : 1 }}
            >
              {line}
            </DialogContentText>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePending}>취소</Button>
          <Button
            color={pending?.intent.destructive ? 'error' : 'primary'}
            onClick={confirmPending}
          >
            {pending?.intent.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default WalletPage
